import React, { MouseEvent, useEffect, useState } from 'react';
import classNames from 'classnames';
import { CaretRightOutlined } from '@easyv/react-icons';
import { isNil } from '@/utils';
import { AssetItem } from '@/type';
import { ROOT_FOLDER_ID } from '../../..';
import { findItemInTreeList } from '@/utils/treeList';

export type UserAssetCategoryItemType = Omit<AssetItem<'folder'>, 'children'> & {
  contentCount: number;
  children: UserAssetCategoryItemType[];
};

interface UserAssetCategoryProps {
  className?: string;
  depth: number;
  data: UserAssetCategoryItemType;

  selectedFolder: null | { id: string; name: string };
  onSelectFolder: (item: { id: string; name: string }) => void;
  extendRender?: (folderId: string) => React.ReactNode;
}

interface UserAssetCategoryItemProps {
  data: UserAssetCategoryItemType;
  depth: number;
  selectedCate: null | { id: string; name: string };
  onSelectFolder: (item: { id: string; name: string }) => void;
  extendRender?: (folderId: string) => React.ReactNode;
}

export function UserAssetCategory({
  className,
  depth,
  data,
  selectedFolder,
  onSelectFolder,
  extendRender,
}: UserAssetCategoryProps) {
  return (
    <div className={className}>
      <UserAssetCategoryItem
        key={data.id}
        depth={++depth}
        selectedCate={selectedFolder}
        data={data}
        extendRender={extendRender}
        onSelectFolder={onSelectFolder}
      />
    </div>
  );
}

function UserAssetCategoryItem({
  selectedCate,
  data,
  depth,
  onSelectFolder,
  extendRender,
}: UserAssetCategoryItemProps) {
  const [isCollapsed, setIsCollapsed] = useState(!(data.id === ROOT_FOLDER_ID));

  const handleCollapsed = (e: MouseEvent<HTMLSpanElement>) => {
    e.stopPropagation();
    setIsCollapsed((prev) => !prev);
  };

  const isSelected = data.id === selectedCate?.id;

  // 如果工作目录是子孙目录或此目录，则展开
  useEffect(() => {
    if (isNil(selectedCate)) return;
    if (findItemInTreeList(data.children, (item) => item.id === selectedCate.id)) {
      setIsCollapsed(false);
    }
  }, [data, selectedCate]);

  return (
    <>
      <div
        className={classNames(
          isSelected
            ? 'bg-primary text-white cursor-default'
            : 'text-textColorBase cursor-pointer hover:text-white hover:bg-bgColorHover',
          'h-8 leading-0 align-middle px-3 relative group flex items-center justify-center',
        )}
        style={{ paddingLeft: `${depth === 1 ? 12 : 12 + 16 * depth - 16 - 16 > 0}px` }}
        onClick={() => {
          onSelectFolder(data);
        }}
        onContextMenu={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        {data.id !== ROOT_FOLDER_ID && (
          <div className='w-4 flex items-center justify-center'>
            {data?.children && data.children.length > 0 && (
              <div className='flex items-center justify-center' onClick={handleCollapsed}>
                <CaretRightOutlined
                  size={10}
                  className={classNames(
                    'cursor-pointer transition-transform duration-300 transform inline-flex text-content4 group-hover:text-white',
                    {
                      'rotate-90': !isCollapsed,
                    },
                  )}
                />
              </div>
            )}
          </div>
        )}

        <span className={classNames('flex-auto cursor-pointer truncate items-center inline-flex')}>
          <span
            className={classNames(
              'inline-block flex-1 truncate ',
              isSelected ? 'text-white' : 'text-baseText',
            )}
          >
            {data.name}
          </span>
          <span className={classNames(isSelected ? 'text-white' : 'text-content3')}>
            {data.contentCount || 0}
          </span>
          <span>{extendRender?.(data.id)}</span>
        </span>
      </div>
      {data?.children &&
        !isCollapsed &&
        data.children.map((item) => (
          <UserAssetCategory
            key={item.id}
            depth={depth}
            data={item}
            selectedFolder={selectedCate}
            extendRender={extendRender}
            onSelectFolder={onSelectFolder}
          />
        ))}
    </>
  );
}
